/**
 * Recorder — capture the canvas to a video file, from inside the page.
 *
 * `canvas.captureStream()` plus MediaRecorder, which is the only route that gets real frames
 * out of a WebGL canvas without a screen recorder in the way. Screen capture would give a
 * window-sized, resampled, VSync-jittered version of a canvas that is already exactly the right
 * size — this takes the frames the renderer actually drew.
 *
 * ONE THING MATTERS MORE THAN THE REST: the stream is driven MANUALLY. Passing a frame rate to
 * captureStream lets the browser sample the canvas on its own schedule, which drops or doubles
 * frames whenever a build hitches — and a shark meshing on a worker will hitch. Requesting 0
 * and calling requestFrame() after each draw means one drawn frame becomes exactly one recorded
 * frame, so the result is smooth even when the capture is not real time.
 */

/** what the timeline is captured at; 30 is plenty for something this slow and halves the size */
export const REEL_FPS = 30;

export interface RecorderOptions {
  /** bits per second. 12 Mbps is generous for 1080x1920 and leaves room for the kelp. */
  bitrate?: number;
  filename?: string;
}

/** codec preferences, best first. Chrome has VP9; Safari only recently got anything at all. */
const CANDIDATES = [
  'video/webm;codecs=vp9',
  'video/webm;codecs=vp8',
  'video/webm',
  'video/mp4;codecs=avc1',
];

export function recordingSupported(): boolean {
  return typeof MediaRecorder !== 'undefined'
    && typeof HTMLCanvasElement.prototype.captureStream === 'function'
    && CANDIDATES.some((t) => MediaRecorder.isTypeSupported(t));
}

export class Recorder {
  private recorder: MediaRecorder | null = null;
  private stream: MediaStream | null = null;
  private track: (MediaStreamTrack & { requestFrame?: () => void }) | null = null;
  private readonly chunks: Blob[] = [];
  private readonly filename: string;
  private frames = 0;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly opts: RecorderOptions = {},
  ) {
    this.filename = opts.filename ?? 'shoreplay-reel';
  }

  get recording(): boolean {
    return this.recorder !== null && this.recorder.state === 'recording';
  }

  get frameCount(): number {
    return this.frames;
  }

  start(): boolean {
    if (!recordingSupported()) {
      console.warn('[reel] this browser cannot record a canvas; run it in Chrome');
      return false;
    }
    // 0 fps: nothing is captured until requestFrame(), which is what keeps drawn frames and
    // recorded frames one to one.
    this.stream = this.canvas.captureStream(0);
    const [track] = this.stream.getVideoTracks();
    this.track = track as MediaStreamTrack & { requestFrame?: () => void };

    const mimeType = CANDIDATES.find((t) => MediaRecorder.isTypeSupported(t));
    this.recorder = new MediaRecorder(this.stream, {
      mimeType,
      videoBitsPerSecond: this.opts.bitrate ?? 12_000_000,
    });
    this.recorder.ondataavailable = (e) => {
      if (e.data.size > 0) this.chunks.push(e.data);
    };
    this.recorder.start();
    this.frames = 0;
    return true;
  }

  /** Call once per drawn frame, AFTER the draw. */
  capture(): void {
    if (!this.recording) return;
    this.track?.requestFrame?.();
    this.frames++;
  }

  /** Stop and hand the file to the browser. Resolves once the download has been triggered. */
  async stop(): Promise<Blob | null> {
    const rec = this.recorder;
    if (!rec || rec.state === 'inactive') return null;

    const done = new Promise<void>((resolve) => {
      rec.onstop = () => resolve();
    });
    rec.stop();
    await done;

    for (const t of this.stream?.getTracks() ?? []) t.stop();
    this.stream = null;
    this.track = null;
    this.recorder = null;

    if (this.chunks.length === 0) return null;
    const blob = new Blob(this.chunks, { type: this.chunks[0].type });
    this.chunks.length = 0;

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${this.filename}.${blob.type.includes('mp4') ? 'mp4' : 'webm'}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Not revoked immediately: Chrome cancels an in-flight download if the URL goes away
    // before it has been read, and the failure is silent.
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    return blob;
  }

  dispose(): void {
    for (const t of this.stream?.getTracks() ?? []) t.stop();
    if (this.recorder && this.recorder.state !== 'inactive') this.recorder.stop();
    this.recorder = null;
    this.stream = null;
    this.track = null;
    this.chunks.length = 0;
  }
}
